import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import "../../../ProductsTab/components/Products/products.css";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Pagination,
  styled,
  useMediaQuery,
  useTheme,
  Drawer,
  Box,
} from "@mui/material";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { debounce } from "lodash";
import { AppContext } from "../../../../store/AppContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import FilterListIcon from "@mui/icons-material/FilterList";

const ColorButton = styled(Button)(() => ({
  color: "white",
  fontSize: "1rem",
  backgroundColor: "#1560ab",
  "&:hover": {
    backgroundColor: "#1560ab",
  },
}));

const Orders = () => {
  const {
    setOrderTabInfo,
    orderTabInfo,
    pageHistory,
    setPageHistory,
    lastVisibleDoc,
    setLastVisibleDoc,
    totalOrders,
    setTotalOrders,
  } = useContext(AppContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { setHeaderData, setLoader } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRangeLabel, setDateRangeLabel] = useState("Last 7 Days");
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 7))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const exportButtonRef = useRef(null);
  const pageSize = 10;

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Pagination states
  // Stores the lastVisibleDoc of the *previous* page to start the *current* page.
  // pageHistory[0] is null, representing the start of the very first page.

  const currentPage = orderTabInfo?.page || 1;

  const filters = [
    "All",
    "Pending",
    "Accepted",
    "Rejected",
    "Cancelled",
    "Expired",
    "Shipped",
  ];

  // Date range presets
  const datePresets = [
    {
      label: "Today",
      getDates: () => {
        const today = new Date();
        return {
          start: new Date(today.setHours(0, 0, 0, 0)),
          end: new Date(today.setHours(23, 59, 59, 999)),
        };
      },
    },
    {
      label: "Yesterday",
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: new Date(yesterday.setHours(0, 0, 0, 0)),
          end: new Date(yesterday.setHours(23, 59, 59, 999)),
        };
      },
    },
    {
      label: "Last 7 Days",
      getDates: () => {
        const start = new Date();
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { start, end: new Date() };
      },
    },
    {
      label: "Last 30 Days",
      getDates: () => {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        return { start, end: new Date() };
      },
    },
    {
      label: "This Month",
      getDates: () => {
        const start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        return { start, end: new Date() };
      },
    },
    {
      label: "Last Month",
      getDates: () => {
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      },
    },
    { label: "Custom Range", getDates: () => null },
  ];

  useEffect(() => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "All orders",
      back: true,
    }));
  }, [setHeaderData]);

  console.log("Orders Rendered", orders);

  // --- Fetching Total Orders Count ---
  const fetchTotalOrdersCount = useCallback(async () => {
    try {
      const ordersCollectionRef = collection(db, "orders");
      let queryConstraints = [];

      // Date range filter
      if (startDate && endDate) {
        queryConstraints.push(
          where("timestamp", ">=", startDate),
          where("timestamp", "<=", endDate)
        );
      }

      // Status filter
      if (statusFilter !== "All") {
        queryConstraints.push(
          where("status", "==", statusFilter.toLowerCase())
        );
      }

      // Search term filter (case-insensitive)
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        queryConstraints.push(
          where("customer_details.name_lower", ">=", searchTermLower),
          where("customer_details.name_lower", "<=", searchTermLower + "\uf8ff")
        );
      }

      const q = query(ordersCollectionRef, ...queryConstraints);
      const snapshot = await getCountFromServer(q);
      const count = snapshot.data().count;

      setTotalOrders(count);
      setOrderTabInfo((prevState) => ({
        ...prevState,
        totalPages: Math.ceil(count / pageSize),
      }));
    } catch (error) {
      console.error("Error fetching total orders count:", error);
    }
  }, [searchTerm, statusFilter, startDate, endDate, setOrderTabInfo, pageSize]);

  const fetchOrders = async (page = 1) => {
    setLoader(true);
    try {
      const ordersCollectionRef = collection(db, "orders");
      let ordersQuery;

      // Handle default view with cursor-based pagination (no filters)
      if (!searchTerm && statusFilter === "All" && !startDate && !endDate) {
        if (
          page > 1 &&
          (!pageHistory[page - 1] || pageHistory.length <= page - 1)
        ) {
          // Fetch all docs up to current page if page history is incomplete
          const allDocsQuery = query(
            ordersCollectionRef,
            orderBy("timestamp", "desc"),
            limit(page * pageSize)
          );
          const allDocsSnapshot = await getDocs(allDocsQuery);
          const allDocs = allDocsSnapshot.docs;

          const startIndex = (page - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          setOrders(
            allDocs
              .slice(startIndex, endIndex)
              .map((doc) => ({ id: doc.id, ...doc.data() }))
          );

          // Rebuild page history
          const newPageDocs = [null]; // First page has no startAfter doc
          for (let i = 1; i <= page; i++) {
            const endIdx = i * pageSize;
            if (endIdx <= allDocs.length) {
              newPageDocs[i] = allDocs[endIdx - 1];
            }
          }
          setPageHistory(newPageDocs);
        } else {
          // Normal cursor-based pagination
          if (page === 1) {
            ordersQuery = query(
              ordersCollectionRef,
              orderBy("timestamp", "desc"),
              limit(pageSize)
            );
          } else {
            ordersQuery = query(
              ordersCollectionRef,
              orderBy("timestamp", "desc"),
              startAfter(pageHistory[page - 1]),
              limit(pageSize)
            );
          }

          const querySnapshot = await getDocs(ordersQuery);
          setOrders(
            querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
          );

          // Update page history
          if (querySnapshot.docs.length > 0) {
            const newPageDocs = [...pageHistory];
            newPageDocs[page] =
              querySnapshot.docs[querySnapshot.docs.length - 1];
            setPageHistory(newPageDocs);
          }
        }

        // Fetch total count only when no filters are applied
        if (!searchTerm && statusFilter === "All" && !startDate && !endDate) {
          fetchTotalOrdersCount();
        }
      }

      // Handle ALL filters (Search, Status, Date) with client-side pagination
      else {
        let queryConstraints = [];

        // Add date range filter (Firestore query)
        // Only apply date range if NOT searching
        if (startDate && endDate && !searchTerm) {
          queryConstraints.push(
            where("timestamp", ">=", startDate),
            where("timestamp", "<=", endDate)
          );
        }

        // Add status filter (Firestore query)
        if (statusFilter !== "All") {
          queryConstraints.push(
            where("status", "==", statusFilter.toLowerCase())
          );
        }

        // Always order by timestamp for filtering
        // This is the only 'orderBy' we can use with a timestamp range
        queryConstraints.push(orderBy("timestamp", "desc"));

        const baseQuery = query(ordersCollectionRef, ...queryConstraints);
        const querySnapshot = await getDocs(baseQuery);

        let allFilteredOrders = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Apply client-side search filter
        // Apply client-side search filter
        if (searchTerm) {
          const searchTermLower = searchTerm.toLowerCase();
          allFilteredOrders = allFilteredOrders.filter((order) => {
            // 1. Check Order ID
            const idMatch = order.id.toLowerCase().includes(searchTermLower);

            // 2. Check Customer Name
            const nameMatch =
              order.customer_details.name_lower.includes(searchTermLower);

            // 3. Check Order Date
            let dateMatch = false;
            if (
              order.timestamp &&
              typeof order.timestamp.toDate === "function"
            ) {
              const dateString = order.timestamp.toDate().toLocaleDateString();
              dateMatch = dateString.includes(searchTermLower);
            }

            // --- NEW SEARCH FIELDS ---

            // 4. Check Customer Email
            const emailMatch = order.customer_details.email
              ?.toLowerCase()
              .includes(searchTermLower);

            // 5. Check Customer Phone Number
            // (Remove +91 if present, for easier searching)
            const phoneMatch = order.customer_details.mobile_number
              ?.replace("+91", "")
              .includes(searchTermLower);

            // 6. Check City
            const cityMatch = order.customer_details.city
              ?.toLowerCase()
              .includes(searchTermLower);

            // 7. Check Pincode
            const pincodeMatch =
              order.customer_details.pincode?.includes(searchTermLower);

            // 8. Check Product Names (loops through all items in the order)
            const productMatch = order.quantity_each?.some((item) =>
              item.product_name.toLowerCase().includes(searchTermLower)
            );

            // Return true if ANY of the fields match
            return (
              idMatch ||
              nameMatch ||
              dateMatch ||
              emailMatch ||
              phoneMatch ||
              cityMatch ||
              pincodeMatch ||
              productMatch
            );
          });
        }

        // Update total count and pagination info
        setTotalOrders(allFilteredOrders.length);
        setOrderTabInfo((prevState) => ({
          ...prevState,
          totalPages: Math.ceil(allFilteredOrders.length / pageSize),
        }));

        // Client-side pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const pageOrders = allFilteredOrders.slice(startIndex, endIndex);
        setOrders(pageOrders);

        // Update page docs for navigation
        if (pageOrders.length > 0) {
          const newPageDocs = [...pageHistory];
          newPageDocs[page] = pageOrders[pageOrders.length - 1];
          setPageHistory(newPageDocs);
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      if (error.code === "failed-precondition") {
        console.error(
          "Firestore index missing or query invalid. Check console for index suggestions."
        );
      }
    } finally {
      setLoader(false);
    }
  };

  // Update the useEffect to match products pattern
  useEffect(() => {
    fetchTotalOrdersCount();
  }, []);

  useEffect(() => {
    fetchOrders(orderTabInfo?.page || currentPage || 1);
  }, [
    orderTabInfo?.page,
    currentPage,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
  ]);

  console.log("Page History:", orders);

  // --- Reset Pagination State ---
  const resetPagination = useCallback(() => {
    setOrderTabInfo((prevState) => ({ ...prevState, page: 1 }));
    setPageHistory([null]); // Reset history to only the start of page 1
    setLastVisibleDoc(null);
    setTotalOrders(0); // Reset total orders, it will be refetched
  }, [setOrderTabInfo]);

  // --- Pagination Handler ---
  const handlePageChange = useCallback(
    async (event, value) => {
      setLoader(true);

      // Case 1: Navigating backward or to an already visited page in history
      if (
        value < currentPage ||
        (value - 1 < pageHistory.length && pageHistory[value - 1] !== undefined)
      ) {
        setOrderTabInfo((prevState) => ({ ...prevState, page: value }));
        // If moving backward, slice history to prevent invalid forward jumps later
        if (value < currentPage && value < pageHistory.length) {
          setPageHistory((prev) => prev.slice(0, value));
        }
        setLoader(false);
        return; // useEffect will handle fetching based on updated currentPage/pageHistory
      }

      // Case 2: Navigating exactly one page forward (standard next button click)
      // and the lastVisibleDoc from the current page is available
      if (value === currentPage + 1 && lastVisibleDoc) {
        setPageHistory((prev) => [...prev, lastVisibleDoc]); // Add the last doc of current page as start for next
        setOrderTabInfo((prevState) => ({ ...prevState, page: value }));
        setLoader(false);
        return; // useEffect will handle fetching based on updated currentPage/pageHistory
      }

      // Case 3: Direct jump forward to a page not yet in history
      if (value > currentPage) {
        // Start with the last visible document of the *current actual page*
        let currentIntermediateLastDoc = lastVisibleDoc; // Use lastVisibleDoc of the CURRENT page (before jump)
        let newPageHistory = [...pageHistory];

        // If lastVisibleDoc is null at the start of a jump forward, it means the current page was already the last page
        if (!currentIntermediateLastDoc) {
          console.warn(
            "Attempted to jump forward from a page that is already the last page. Redirecting to last known page."
          );
          setOrderTabInfo((prev) => ({ ...prev, page: newPageHistory.length })); // Go to the last page we have history for
          setLoader(false);
          return;
        }

        // Fetch intermediate documents to build up pageHistory until target page - 1
        for (let i = currentPage; i < value; i++) {
          const ordersCollectionRef = collection(db, "orders");
          let queryConstraints = [];

          if (startDate && endDate) {
            queryConstraints.push(
              where("timestamp", ">=", startDate),
              where("timestamp", "<=", endDate)
            );
          }
          if (statusFilter !== "All") {
            queryConstraints.push(
              where("status", "==", statusFilter.toLowerCase())
            );
          }
          if (searchTerm) {
            const searchTermLower = searchTerm.toLowerCase();
            queryConstraints.push(
              where("customer_details.nameLower", ">=", searchTermLower),
              where(
                "customer_details.nameLower",
                "<=",
                searchTermLower + "\uf8ff"
              )
            );
          }
          queryConstraints.push(orderBy("timestamp", "desc"));

          // Use the currentIntermediateLastDoc to start after for the *next* intermediate page
          // This is crucial: we need the last doc of the *previous* page to start the *current* intermediate fetch.
          if (currentIntermediateLastDoc) {
            queryConstraints.push(startAfter(currentIntermediateLastDoc));
          } else {
            // This means we ran out of documents in a previous intermediate fetch.
            // Don't go to the target page if we can't build its start doc.
            console.warn(
              `Ran out of documents while building page history for page ${
                i + 1
              }. Cannot jump to requested page.`
            );
            const actualTotalPages = newPageHistory.length; // The last page we successfully found a start doc for
            setOrderTabInfo((prev) => ({
              ...prev,
              page: actualTotalPages > 0 ? actualTotalPages : 1,
            }));
            setLoader(false);
            return;
          }

          queryConstraints.push(limit(pageSize));

          const q = query(ordersCollectionRef, ...queryConstraints);
          const snapshot = await getDocs(q);

          if (snapshot.docs.length > 0) {
            currentIntermediateLastDoc =
              snapshot.docs[snapshot.docs.length - 1];

            // Add the last doc of this intermediate page to history.
            // This doc will be the `startAfter` for the next page (`i + 1`).
            // Ensure we don't add duplicates if already present (unlikely with this logic, but safe).
            if (newPageHistory.length <= i) {
              // Check if we need to add a new entry for page i + 1
              newPageHistory.push(currentIntermediateLastDoc);
            } else {
              newPageHistory[i] = currentIntermediateLastDoc; // Update existing entry if somehow jumped back and forth
            }

            // If this intermediate page is less than a full page, it means it's the last available data
            if (snapshot.docs.length < pageSize) {
              console.log(
                `Reached end of data at intermediate page ${
                  i + 1
                }. Target page ${value} may be beyond available data.`
              );
              // Adjust total pages if this happens during a jump and the current totalPages is too high
              const actualTotalPages = i + 1; // Current page (i) + 1 for 1-based index
              setOrderTabInfo((prev) => ({
                ...prev,
                totalPages: actualTotalPages,
              }));
              setOrderTabInfo((prevState) => ({
                ...prevState,
                page: actualTotalPages,
              })); // Go to the last *actual* page
              setPageHistory(newPageHistory.slice(0, actualTotalPages)); // Trim history to available pages
              setLoader(false);
              return; // Stop here, we found the true last page
            }
          } else {
            // No documents fetched for this intermediate page.
            // This means the target page 'value' is beyond the available data.
            console.warn(
              `No documents for intermediate page ${
                i + 1
              }. Target page ${value} is out of bounds.`
            );
            const actualTotalPages = newPageHistory.length; // The number of pages for which we successfully built history
            setOrderTabInfo((prev) => ({
              ...prev,
              totalPages: actualTotalPages,
            })); // Set total pages to the last available page
            setOrderTabInfo((prevState) => ({
              ...prevState,
              page: actualTotalPages > 0 ? actualTotalPages : 1,
            })); // Go to the last available page or page 1
            setPageHistory(newPageHistory.slice(0, actualTotalPages)); // Trim history
            setLoader(false);
            return; // Stop, as we can't reach the requested page
          }
        }

        setPageHistory(newPageHistory); // Set the updated history
        setOrderTabInfo((prevState) => ({ ...prevState, page: value })); // Go to the target page
      }
      setLoader(false);
    },
    [
      currentPage,
      lastVisibleDoc, // lastVisibleDoc of the current page is needed for next page navigation
      pageHistory,
      setOrderTabInfo,
      startDate,
      endDate,
      statusFilter,
      searchTerm,
      pageSize,
      setLoader,
      orderTabInfo.totalPages, // Dependency for adjusting totalPages
    ]
  );

  // --- Search Handler (Debounced) ---
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      resetPagination(); // Reset pagination on new search
    }, 500),
    [resetPagination]
  );

  // Handle date preset selection
  const handleDatePresetClick = (preset) => {
    if (preset.getDates()) {
      const { start, end } = preset.getDates();
      setStartDate(start);
      setEndDate(end);
      setDateRangeLabel(preset.label);
    }
    setAnchorEl(null);
    resetPagination(); // Reset pagination on date range change
  };

  // Handle custom range selection
  const handleCustomRangeChange = () => {
    setDateRangeLabel("Custom Range");
    setAnchorEl(null);
    // Note: If you have a custom date picker UI, you'd trigger it here
    // For now, it just changes the label. Actual date inputs are separate.
  };

  // Handle custom date input changes
  const handleCustomDateChange = useCallback(
    (type, date) => {
      if (type === "start") {
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      resetPagination(); // Reset pagination on custom date change
    },
    [resetPagination]
  );

  // Handle status filter change
  const handleStatusChange = (filter) => {
    setStatusFilter(filter);
    resetPagination(); // Reset pagination on status filter change
  };

  // Format date range for display
  const formatDateRange = () => {
    if (dateRangeLabel !== "Custom Range") {
      return dateRangeLabel;
    }
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  // Export orders to Excel
  const exportToExcel = () => {
    const exportData = orders.map((order) => {
      const customer = order.customer_details || {};
      const shipping = order.shipping_details || {};
      const payment = order.payment_details || {};
      const items = order.quantity_each || [];

      return {
        "Order ID": order.id || "N/A",
        "Order Date": order.timestamp?.toDate().toLocaleString() || "N/A",
        "Customer Name": customer.name || "N/A",
        "Customer Email": customer.email || "N/A",
        "Customer Phone": customer.phone || "N/A",
        "Shipping Address":
          [
            shipping.address_line1,
            shipping.address_line2,
            shipping.city,
            shipping.state,
            shipping.postal_code,
            shipping.country,
          ]
            .filter(Boolean)
            .join(", ") || "N/A",
        "Payment Method": order.payment_mode || "N/A",
        "Payment Status": payment.status || "N/A",
        "Transaction ID": payment.transaction_id || "N/A",
        "Order Status": order.status || "N/A",
        Subtotal: `₹${((order.items_total || 0) - (order.delivery || 0) + (order.coupon_discount || 0)).toFixed(2)}`,
        Tax: `₹${order.tax_amount?.toFixed(2) || "0.00"}`,
        Shipping: `₹${order.delivery?.toFixed(2) || "0.00"}`,
        Discount: `₹${order.coupon_discount?.toFixed(2) || "0.00"}`,
        "Total Amount": `₹${order.items_total?.toFixed(2) || "0.00"}`,
        "Items Count": items.length,
        "Items Details": items
          .map(
            (item) =>
              `${item.quantity}x ${item.product_name} (₹${item.product_price})`
          )
          .join("; "),
        "Coupon Code": order.coupon_code || "N/A",
        Notes: order.notes || "N/A",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    const wscols = [
      { wch: 15 }, // Order ID
      { wch: 20 }, // Order Date
      { wch: 20 }, // Customer Name
      { wch: 25 }, // Customer Email
      { wch: 15 }, // Customer Phone
      { wch: 40 }, // Shipping Address
      { wch: 15 }, // Payment Method
      { wch: 15 }, // Payment Status
      { wch: 20 }, // Transaction ID
      { wch: 15 }, // Order Status
      { wch: 10 }, // Subtotal
      { wch: 10 }, // Tax
      { wch: 10 }, // Shipping
      { wch: 10 }, // Discount
      { wch: 12 }, // Total Amount
      { wch: 10 }, // Items Count
      { wch: 50 }, // Items Details
      { wch: 15 }, // Coupon Code
      { wch: 30 }, // Notes
    ];
    worksheet["!cols"] = wscols;

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const dateStr = new Date().toISOString().split("T")[0];
    saveAs(data, `orders_export_${dateStr}.xlsx`);
  };
  const MobileOrderCard = ({ order }) => (
    <div
      onClick={() => {
        setHeaderData((prevState) => ({
          ...prevState,
          leftText: "Order details",
          rightActionText: "Accept order",
          back: false,
          leftAction: () => {
            setOrderTabInfo((prevState) => ({
              ...prevState,
              viewOrder: false,
              selectedOrder: null,
            }));
            setHeaderData({
              leftText: "",
              rightActionText: "",
              leftAction: () => {},
              rightAction: () => {
                setOrderTabInfo((prevState) => ({
                  ...prevState,
                  trigger: true,
                }));
              },
            });
          },
        }));

        setOrderTabInfo((prevState) => ({
          ...prevState,
          viewOrder: true,
          selectedOrder: order,
        }));
      }}
      style={{
        background: "white",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "12px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <span style={{ color: "#023DE1", fontSize: "14px", fontWeight: "500" }}>
          #{order.id.substring(0, 10)}...
        </span>
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor:
              order.status?.toLowerCase() === "shipped"
                ? "#E8F5E9"
                : order.status?.toLowerCase() === "accepted"
                ? "#E8F5E9"
                : order.status?.toLowerCase() === "pending"
                ? "#FFF3E0"
                : order.status?.toLowerCase() === "expired"
                ? "#F5F5F5"
                : "#FFEBEE",
            color:
              order.status?.toLowerCase() === "shipped"
                ? "#2E7D32"
                : order.status?.toLowerCase() === "accepted"
                ? "#388E3C"
                : order.status?.toLowerCase() === "pending"
                ? "#FB8C00"
                : order.status?.toLowerCase() === "expired"
                ? "#757575"
                : "#D32F2F",
          }}
        >
          {order.status}
        </span>
      </div>

      <div style={{ fontSize: "14px", color: "#666", marginBottom: "4px" }}>
        {order.customer_details?.name || "N/A"}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "12px", color: "#999" }}>
          {order?.timestamp &&
            new Date(order.timestamp.seconds * 1000).toLocaleDateString()}
          {" • "}
          {order.quantity_each?.length || 0} items
        </div>
        <div style={{ fontSize: "16px", fontWeight: "600" }}>
          ₹{order.items_total?.toFixed(2) || "0.00"}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "16px" : "20px",
        width: "100%",
        maxWidth: "1400px",
        margin: "auto",
        padding: isMobile ? "0" : "0 20px",
      }}
    >
      {/* Search and Filter Bar */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <FormControl
            size="small"
            variant="outlined"
            style={{
              width: isMobile ? "100%" : "auto",
              minWidth: isMobile ? "100%" : "200px",
            }}
          >
            <InputLabel>Search</InputLabel>
            <OutlinedInput
              size="small"
              onChange={(e) => debouncedSearch(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              }
              label="Search"
            />
          </FormControl>

          {!isMobile && (
            <Button
              variant="outlined"
              startIcon={<DateRangeIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              style={{ textTransform: "none", height: "40px" }}
            >
              {formatDateRange()}
            </Button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {isMobile && (
            <Button
              variant="outlined"
              onClick={() => setMobileFiltersOpen(true)}
              startIcon={<FilterListIcon />}
              style={{ flex: 1 }}
            >
              Filters
            </Button>
          )}

          <CustomButton
            variant="contained"
            onClick={exportToExcel}
            ref={exportButtonRef}
            sx={{
              textTransform: "none",
              flex: isMobile ? 1 : "none",
              fontSize: isMobile ? "0.875rem" : "1rem",
            }}
          >
            {isMobile ? "Export" : "Export Orders"}
          </CustomButton>
        </div>
      </div>

      {/* Desktop Date Range Button */}
      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {datePresets.map((preset) => (
            <MenuItem
              key={preset.label}
              onClick={() =>
                preset.label === "Custom Range"
                  ? handleCustomRangeChange()
                  : handleDatePresetClick(preset)
              }
            >
              {preset.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Custom Date Range Picker */}
      {dateRangeLabel === "Custom Range" && !isMobile && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="date"
            value={startDate.toISOString().split("T")[0]}
            onChange={(e) => setStartDate(new Date(e.target.value))}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
          <span>to</span>
          <input
            type="date"
            value={endDate.toISOString().split("T")[0]}
            onChange={(e) => setEndDate(new Date(e.target.value))}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
        </div>
      )}

      {/* Status Filter Buttons - Desktop Only */}
      {!isMobile && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {filters.map((filter, index) => (
            <div
              key={index}
              className={`${
                statusFilter.toLowerCase() === filter.toLowerCase()
                  ? "filter-button filter-active"
                  : "filter-button"
              }`}
              onClick={() => {
                setStatusFilter(filter);
                setOrderTabInfo((prevState) => ({ ...prevState, page: 1 }));
              }}
              style={{
                padding: "10px 20px",
                borderRadius: "20px",
                cursor: "pointer",
              }}
            >
              <h5
                style={{
                  fontWeight: "400",
                  color:
                    statusFilter.toLowerCase() === filter.toLowerCase()
                      ? "white"
                      : "#9f9e9f",
                  margin: 0,
                }}
              >
                {filter}
              </h5>
            </div>
          ))}
        </div>
      )}

      {/* Mobile Filters Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
        >
          <Box sx={{ p: 2, pb: 3 }}>
            <h3 style={{ margin: "0 0 16px 0" }}>Filters</h3>

            {/* Date Range */}
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ margin: "0 0 12px 0", color: "#666" }}>
                Date Range
              </h4>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<DateRangeIcon />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                style={{ textTransform: "none", marginBottom: "8px" }}
              >
                {formatDateRange()}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {datePresets.map((preset) => (
                  <MenuItem
                    key={preset.label}
                    onClick={() => {
                      if (preset.label === "Custom Range") {
                        handleCustomRangeChange();
                      } else {
                        handleDatePresetClick(preset);
                      }
                      setMobileFiltersOpen(false);
                    }}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>

            {/* Status Filter */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", color: "#666" }}>Status</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {filters.map((filter, index) => (
                  <Button
                    key={index}
                    variant={statusFilter === filter ? "contained" : "outlined"}
                    onClick={() => {
                      setStatusFilter(filter);
                      setOrderTabInfo((prevState) => ({
                        ...prevState,
                        page: 1,
                      }));
                      setMobileFiltersOpen(false);
                    }}
                    style={{
                      textTransform: "none",
                      borderRadius: "20px",
                      fontSize: "0.875rem",
                    }}
                  >
                    {filter}
                  </Button>
                ))}
              </div>
            </div>
          </Box>
        </Drawer>
      )}

      {/* Orders Display */}
      {isMobile ? (
        // Mobile View - Cards
        <div>
          {orders.map((order) => (
            <MobileOrderCard key={order.id} order={order} />
          ))}
        </div>
      ) : (
        // Desktop View - Table
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: isTablet ? "10px 5px" : "10px",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            overflowX: isTablet ? "auto" : "visible",
          }}
        >
          <div
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              minWidth: isTablet ? "700px" : "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: isTablet ? "13px" : "14px",
              }}
            >
              <thead>
                <tr style={{ background: "#f5f5f5", height: "48px" }}>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                      padding: "12px",
                    }}
                  >
                    Order ID
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Customer
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Items
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Payment
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      fontSize: isTablet ? "13px" : "14px",
                      fontWeight: "500",
                    }}
                  >
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody className="scrollable-tbody">
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      height: "56px",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        color: "#023DE1",
                        fontSize: isTablet ? "13px" : "14px",
                        fontWeight: "400",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setHeaderData((prevState) => ({
                          ...prevState,
                          leftText: "Order details",
                          rightActionText: "Accept order",
                          back: false,
                          leftAction: () => {
                            setOrderTabInfo((prevState) => ({
                              ...prevState,
                              viewOrder: false,
                              selectedOrder: null,
                            }));
                            setHeaderData({
                              leftText: "",
                              rightActionText: "",
                              leftAction: () => {},
                              rightAction: () => {
                                setOrderTabInfo((prevState) => ({
                                  ...prevState,
                                  trigger: true,
                                }));
                              },
                            });
                          },
                        }));

                        setOrderTabInfo((prevState) => ({
                          ...prevState,
                          viewOrder: true,
                          selectedOrder: order,
                        }));
                      }}
                      title={`Order ID: ${order.id}`}
                    >
                      #
                      {order.id.length > 15
                        ? `${order.id.substring(0, 15)}...`
                        : order.id}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: isTablet ? "13px" : "14px",
                      }}
                    >
                      {order?.timestamp &&
                        (() => {
                          const orderDate = order.timestamp.toDate();
                          const today = new Date();
                          const isToday =
                            orderDate.getDate() === today.getDate() &&
                            orderDate.getMonth() === today.getMonth() &&
                            orderDate.getFullYear() === today.getFullYear();

                          const formattedDate = orderDate.toLocaleString(
                            "en-US",
                            {
                              day: isToday ? undefined : "2-digit",
                              month: isToday ? undefined : "short",
                              year: isToday ? undefined : "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          );

                          return isToday
                            ? `Today, ${formattedDate}`
                            : ` ${formattedDate}`;
                        })()}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: isTablet ? "13px" : "14px",
                      }}
                    >
                      {order.customer_details?.name || "N/A"}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: isTablet ? "13px" : "14px",
                      }}
                    >
                      {order.quantity_each?.length || 0}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: isTablet ? "13px" : "14px",
                      }}
                    >
                      {order.payment_mode || "N/A"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: isTablet ? "4px 8px" : "6px 12px",
                          borderRadius: "12px",
                          fontSize: isTablet ? "13px" : "14px",
                          fontWeight: "500",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor:
                            order.status?.toLowerCase() === "shipped"
                              ? "#E8F5E9"
                              : order.status?.toLowerCase() === "accepted"
                              ? "#E8F5E9"
                              : order.status?.toLowerCase() === "pending"
                              ? "#FFF3E0"
                              : order.status?.toLowerCase() === "expired"
                              ? "#F5F5F5"
                              : "#FFEBEE",
                          color:
                            order.status?.toLowerCase() === "shipped"
                              ? "#2E7D32"
                              : order.status?.toLowerCase() === "accepted"
                              ? "#388E3C"
                              : order.status?.toLowerCase() === "pending"
                              ? "#FB8C00"
                              : order.status?.toLowerCase() === "expired"
                              ? "#757575"
                              : "#D32F2F",
                        }}
                      >
                        ● {order.status}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: isTablet ? "13px" : "14px",
                        fontFamily: "sans-serif",
                      }}
                    >
                      ₹{order.items_total?.toFixed(2) || "0.00"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-end",
          marginTop: "20px",
          paddingBottom: isMobile ? "20px" : "0",
        }}
      >
        <Pagination
          count={orderTabInfo?.totalPages}
          page={orderTabInfo?.page}
          onChange={(e, value) =>
            setOrderTabInfo((prevState) => ({
              ...prevState,
              page: value,
            }))
          }
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </div>
    </div>
  );
};

export default Orders;
